import { CDN_ROOT, getCdnImageUrl } from "../features/images";
import { getGenderKey } from "../features/utils";
import { Gender } from "./gender";
import { Physiology } from "./physiology";
import { FunctionFactory } from "survey-core";
import { loc } from "../i18n/survey";

enum BodyType {
  Skinny = 'Skinny',
  SkinnyFat = 'Skinny Fat',
  Defined = 'Defined',
  Athletic = 'Athletic',
  Bulky = 'Bulky',
  Overweight = 'Overweight',
}

const bodyTypeQuestion = {
  name: "bodytype",
  title: `What best matches your <div class="highlight">current body</div> type?`,
  description: `No right or wrong – we use this to better understand your starting point.`,
  descriptionLocation: 'underTitle',
  type: "imagepicker",
  showLabel: true,
  isRequired: true,
};

const bodyTypeMaleQuestion = {
  ...bodyTypeQuestion,
  visibleIf: `getGender({gender},{physiology}) = "${Gender.Male}"`,
  requiredIf: `getGender({gender},{physiology}) = "${Gender.Male}"`,
  choices: [
    {
      "value": BodyType.Skinny,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/skinny.png`,
      "text": "Skinny",
    },
    {
      "value": BodyType.SkinnyFat,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/skinnyfat.png`,
      "text": "Skinny Fat",
    },
    {
      "value": BodyType.Bulky,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/bulky.jpg`,
      "text": "Bulky",
    },
    {
      "value": BodyType.Overweight,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/overweight.jpg`,
      "text": "Overweight",
    },
  ]
};

const bodyTypeFemaleQuestion = {
  ...bodyTypeQuestion,
  visibleIf: `getGender({gender},{physiology}) = "${Gender.Female}"`,
  requiredIf: `getGender({gender},{physiology}) = "${Gender.Female}"`,
  choices: [
    {
      "value": BodyType.Skinny,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/skinny.jpg`,
      "text": "Skinny",
    },
    {
      "value": BodyType.SkinnyFat,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/skinnyfat.jpg`,
      "text": "Skinny Fat",
    },
    {
      "value": BodyType.Bulky,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/bulky.png`,
      "text": "Bulky",
    },
    {
      "value": BodyType.Overweight,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/overweight.jpg`,
      "text": "Overweight",
    },
  ]
};


const bodyTypeBothQuestion = {
  ...bodyTypeQuestion,
  
  choices: [
    {
      "value": BodyType.Skinny,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/skinny.png`,
      "text": "Skinny",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Male}"`,
    },
    {
      "value": BodyType.SkinnyFat,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/skinnyfat.png`,
      "text": "Skinny Fat",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Male}"`,
    },
    {
      "value": BodyType.Bulky,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/bulky.jpg`,
      "text": "Bulky",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Male}"`,
    },
    {
      "value": BodyType.Overweight,
      "imageLink": `${CDN_ROOT}/images/bodytype/Male/overweight.jpg`,
      "text": "Overweight",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Male}"`,
    },
    {
      "value": BodyType.Skinny,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/skinny.jpg`,
      "text": "Skinny",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Female}"`,
    },
    {
      "value": BodyType.SkinnyFat,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/skinnyfat.jpg`,
      "text": "Skinny Fat",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Female}"`,
    },
    {
      "value": BodyType.Bulky,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/bulky.png`,
      "text": "Bulky",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Female}"`,
    },
    {
      "value": BodyType.Overweight,
      "imageLink": `${CDN_ROOT}/images/bodytype/Female/overweight.jpg`,
      "text": "Overweight",
      visibleIf:`getGender({gender},{physiology}) = "${Gender.Female}"`,
    },
  ]

};

const bodyTypeCarouselQuestion = {
  name: "bodytype",
  title: loc("quiz.bodytype.title"),
  description: loc("quiz.bodytype.description"),
  descriptionLocation: 'underTitle',
  type: "carousel",
  isRequired: true,
  choices: [
    {
      value: BodyType.Skinny,
      text: loc("quiz.bodytype.male.slender.text"),
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/male-bodytype/1.png`,
      description: loc("quiz.bodytype.male.slender.description"),
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Male}`,
    },
    {
      value: BodyType.SkinnyFat,
      text: loc("quiz.bodytype.male.skinnyFat.text"),
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/male-bodytype/2.png`,
      description: loc("quiz.bodytype.male.skinnyFat.description"),
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Male}`,
    },
    {
      value: BodyType.Defined,
      text: loc("quiz.bodytype.male.defined.text"),
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/male-bodytype/3.png`,
      description: loc("quiz.bodytype.male.defined.description"),
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Male}`,
    },
    {
      value: BodyType.Athletic,
      text: loc("quiz.bodytype.male.athletic.text"),
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/male-bodytype/4.png`,
      description: loc("quiz.bodytype.male.athletic.description"),
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Male}`,
    },
    {
      value: BodyType.Bulky,
      text: loc("quiz.bodytype.male.bulky.text"),
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/male-bodytype/5.png`,
      description: loc("quiz.bodytype.male.bulky.description"),
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Male}`,
    },
    {
      value: BodyType.Overweight,
      text: loc("quiz.bodytype.male.softHeavy.text"),
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/male-bodytype/6.png`,
      description: loc("quiz.bodytype.male.softHeavy.description"),
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Male}`,
    },


    {
      value: BodyType.Skinny,
      text: loc("quiz.bodytype.female.slender.text"),
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/female-bodytype/1.png`,
      description: loc("quiz.bodytype.female.slender.description"),
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Female}`,
    },
    {
      value: BodyType.SkinnyFat,
      text: loc("quiz.bodytype.female.skinnyFat.text"),
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/female-bodytype/2.png`,
      description: loc("quiz.bodytype.female.skinnyFat.description"),
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Female}`,
    },
    {
      value: BodyType.Defined,
      text: loc("quiz.bodytype.female.toned.text"),
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/female-bodytype/3.png`,
      description: loc("quiz.bodytype.female.toned.description"),
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Female}`,
    },
    {
      value: BodyType.Athletic,
      text: loc("quiz.bodytype.female.strongBalanced.text"),
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/female-bodytype/4.png`,
      description: loc("quiz.bodytype.female.strongBalanced.description"),
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Female}`,
    },
    {
      value: BodyType.Bulky,
      text: loc("quiz.bodytype.female.solidCurvy.text"),
      imageUrl: getCdnImageUrl("images/new-quiz-bodytype-images-2025/female-bodytype/5.png"),
      description: loc("quiz.bodytype.female.solidCurvy.description"),
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Female}`,
    },
    {
      value: BodyType.Overweight,
      text: loc("quiz.bodytype.female.softHeavy.text"),
      imageUrl: `${CDN_ROOT}/images/new-quiz-bodytype-images-2025/female-bodytype/6.png`,
      description: loc("quiz.bodytype.female.softHeavy.description"),
      visibleIf: `getGender({gender},{physiology}) = ${Gender.Female}`,
    },
  ]
};

function getGender(params: any[]): string {
  if (params === undefined || params === null) return "";
  const gender = params[0];
  const physiology = params[1];
  return getGenderKey(gender, physiology);
}

FunctionFactory.Instance.register('getGender', getGender);


export {
  BodyType,
  bodyTypeMaleQuestion,
  bodyTypeFemaleQuestion,
  bodyTypeBothQuestion,
  bodyTypeCarouselQuestion
}
